import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@moe/oss/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@moe/oss/ui/form";
import { Label } from "@moe/oss/ui/label";
import { RadioGroup, RadioGroupItem } from "@moe/oss/ui/radio-group";
import { Textarea } from "@moe/oss/ui/textarea";
import { sb } from "@web/lib/supabase";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const reportSchema = z.object({
  type: z.enum(["GUIDELINES", "SPAM", "ILLEGAL", "OTHER"], {
    required_error: "Please select a report type."
  }),
  content: z.string().max(4096, "Keep your report to be under 4096 characters.").optional()
});
type ReportSchema = z.infer<typeof reportSchema>;

export function ReportModal({ characterID }: { characterID: number }) {
  const { closeModal } = useAppContext();
  const form = useForm<ReportSchema>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      type: undefined,
      content: undefined
    }
  });

  const sendReport = async (content: string, characterID: number) => {
    const { error } = await sb.from("character_reports").insert({ content: content, character_id: characterID });
    if (error && error.message.includes("duplicate key value violates unique constraint")) {
      toast.info("You have already reported this character.");
      return;
    }
    if (error) throw error;
    toast.success("Report sent!");
    closeModal();
  };

  return (
    <div className="bg-float border-line text-tx-primary w-full space-y-4 rounded-xl border p-4 shadow-md md:w-96">
      <h3 className="text-tx-primary mb-2 text-lg font-semibold">Report Character</h3>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => sendReport(`[${data.type}] ${data.content || ""}`, characterID))}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="space-y-2">
                    <FormItem className="flex items-center space-x-3">
                      <FormControl>
                        <RadioGroupItem value="GUIDELINES" id="guidelines" />
                      </FormControl>
                      <Label className="cursor-pointer" htmlFor="guidelines">
                        Breaks Community Guidelines
                      </Label>
                    </FormItem>
                    <FormItem className="flex items-center space-x-3">
                      <FormControl>
                        <RadioGroupItem value="SPAM" id="spam" />
                      </FormControl>
                      <Label className="cursor-pointer" htmlFor="spam">
                        Spam / Very Low Quality
                      </Label>
                    </FormItem>
                    <FormItem className="flex items-center space-x-3">
                      <FormControl>
                        <RadioGroupItem value="ILLEGAL" id="illegal" />
                      </FormControl>
                      <Label className="cursor-pointer" htmlFor="illegal">
                        Illegal Activities
                      </Label>
                    </FormItem>
                    <FormItem className="flex items-center space-x-3">
                      <FormControl>
                        <RadioGroupItem value="OTHER" id="other" />
                      </FormControl>
                      <Label className="cursor-pointer" htmlFor="other">
                        Other
                      </Label>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Help speed up the review by entering a more detailed reason here (optional)."
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Submit Report
          </Button>
        </form>
      </Form>
    </div>
  );
}
